import { SEO } from "../components/SEO";
import { PageHeader } from "../components/PageHeader";
import { CTA } from "../components/sections/CTA";
import { site } from "../lib/site";
import { breadcrumbJsonLd, localBusinessJsonLd } from "../lib/structured-data";
import { IconCheck, IconLeaf, IconShield, IconSparkle } from "../components/Icon";

export function APropos() {
  return (
    <>
      <SEO
        title={`À propos - ${site.name}, société de nettoyage à Vichy depuis ${site.foundingYear}`}
        description={`Anexya, agent d'entretien professionnel à Vichy depuis ${site.foundingYear}. Un interlocuteur unique, de la proximité et un engagement qualité au service des entreprises et particuliers de l'Allier.`}
        path="/a-propos"
        jsonLd={[
          localBusinessJsonLd(),
          breadcrumbJsonLd([
            { name: "Accueil", url: `${site.baseUrl}/` },
            { name: "À propos", url: `${site.baseUrl}/a-propos` },
          ]),
        ]}
      />
      <PageHeader
        eyebrow="À propos"
        title={`${site.name}, votre partenaire propreté à Vichy depuis ${site.foundingYear}`}
        subtitle="Une entreprise familiale, ancrée localement, qui a fait de la relation de confiance et de la régularité son identité."
        breadcrumbs={[{ label: "Accueil", to: "/" }, { label: "À propos" }]}
      />

      <section className="section bg-white">
        <div className="container-page grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 space-y-6 text-ink-muted text-lg leading-relaxed">
            <p>
              Fondée en {site.foundingYear} à Vichy, {site.name} accompagne les TPE, syndics,
              professions libérales et particuliers de l'Allier dans l'entretien quotidien de
              leurs locaux et domiciles. Nous croyons qu'un lieu propre est d'abord une
              affaire de rigueur et de relation humaine.
            </p>
            <p>
              Nous avons volontairement gardé une structure à taille humaine : chaque client a un
              interlocuteur identifié et, autant que possible, le même intervenant d'une semaine
              sur l'autre. C'est la condition d'une qualité constante.
            </p>
            <p>
              Nous intervenons avec un matériel professionnel et une organisation rigoureuse.
              Nous privilégions les produits éco-labellisés et les microfibres pour limiter
              l'impact environnemental sans compromis sur l'hygiène.
            </p>
          </div>
          <aside className="lg:col-span-5 grid grid-cols-2 gap-4 content-start">
            <Stat big="Vichy" small="entreprise locale" />
            <Stat big="30 km" small="rayon depuis Vichy" />
            <Stat big="100 %" small="satisfait ou refait" />
          </aside>
        </div>
      </section>

      <section className="section bg-surface-soft">
        <div className="container-page">
          <header className="max-w-2xl">
            <span className="eyebrow">Nos valeurs</span>
            <h2 className="mt-4">Ce qui nous guide au quotidien</h2>
          </header>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <Value
              icon={IconShield}
              title="Confiance"
              text="Un interlocuteur de confiance, discret et régulier. Le même visage chaque semaine."
            />
            <Value
              icon={IconSparkle}
              title="Exigence"
              text="Nous contrôlons, ajustons et garantissons le résultat. Une non-conformité = une reprise sous 24 h."
            />
            <Value
              icon={IconLeaf}
              title="Responsabilité"
              text="Produits éco-labellisés, dosage maîtrisé, circuits courts dès que possible."
            />
            <Value
              icon={IconCheck}
              title="Proximité"
              text="Basés à Vichy, nous connaissons nos clients et notre territoire. Vous n'êtes pas un numéro."
            />
          </div>
        </div>
      </section>

      <CTA />
    </>
  );
}

function Stat({ big, small }: { big: string; small: string }) {
  return (
    <div className="rounded-2xl bg-surface-soft border border-slate-200 p-5 text-center">
      <div className="font-display font-bold text-3xl text-brand-700">{big}</div>
      <div className="mt-1 text-xs uppercase tracking-wider text-ink-muted">{small}</div>
    </div>
  );
}

function Value({
  icon: Icon,
  title,
  text,
}: {
  icon: typeof IconShield;
  title: string;
  text: string;
}) {
  return (
    <div className="card card-hover">
      <div className="grid h-11 w-11 place-items-center rounded-xl bg-brand-50 text-brand-600">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="mt-4 text-lg">{title}</h3>
      <p className="mt-2 text-sm text-ink-muted leading-relaxed">{text}</p>
    </div>
  );
}
